<template>
  <div>
    <b-row class="mb-1">
      <b-col v-if="$can('Save', 'Companies')">
        <b-button v-b-modal.modal-newCompany variant="primary">
          <feather-icon icon="PlusIcon" class="mr-25" />
          <span>Add Company</span>
        </b-button>
      </b-col>
    </b-row>
    <b-card title="Companies">
      <b-row>
        <b-col md="3">
          <b-form-group>
            <b-form-input
              v-model="filter"
              placeholder="Search"
              type="text"
              class="d-inline-block mr-1"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-table
            striped
            :items="items"
            :fields="fields"
            :filter="filter"
            :per-page="perPage"
            :current-page="currentPage"
            caption-top
            :responsive="true"
          >
            <template
              v-if="$can('Delete', 'Companies') || $can('Update', 'Companies')"
              v-slot:cell(actions)="data"
            >
              <b-row>
                <b-dropdown
                  variant="link"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-item
                    v-if="$can('Update', 'Companies')"
                    href="#"
                    variant="success"
                    @click="editCompanyModal(data.item.id)"
                  >
                    <feather-icon class="yellow-icon" icon="EditIcon" />
                    <span class="align-middle ml-50">Edit</span>
                  </b-dropdown-item>

                  <b-dropdown-item
                    v-if="$can('Delete', 'Companies')"
                    href="#"
                    variant="danger"
                    @click="deleteCompany(data.item.id, data.item.name)"
                  >
                    <feather-icon class="blue-icon" icon="TrashIcon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </b-row>
            </template>
          </b-table>
          <div class="d-flex justify-content-between flex-wrap">
            <div class="d-flex align-items-center mb-0 mt-1">
              <span class="text-nowrap"> Showing 1 to &nbsp; </span>
              <b-form-select
                id="per-page-select"
                v-model="perPage"
                :options="[5, 10, 15, 20, { value: 100, text: 'Show all' }]"
                size="sm"
              ></b-form-select>
              <span class="text-nowrap">
                &nbsp; of {{ items.length }} entries
              </span>
            </div>
            <div>
              <b-pagination
                v-model="currentPage"
                :total-rows="rows"
                :per-page="perPage"
              />
            </div>
          </div>
        </b-col>
      </b-row>

      <b-col>
        <b-modal
          id="modal-newCompany"
          ref="modal"
          hide-footer
          title="Add New Company"
          size="lg"
        >
          <validation-observer ref="simpleRules">
            <form ref="form">
              <b-row>
                <b-col md="6">
                  <b-form-group label="Name" label-for="name">
                    <validation-provider
                      #default="{ errors }"
                      name="name"
                      rules="required"
                    >
                      <b-form-input
                        v-on:keypress="isLetter($event)"
                        v-on:paste="
                          (e) => {
                            e.preventDefault();
                            return false;
                          }
                        "
                        id="name-input"
                        v-model="newCompany.name"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Company Code" label-for="company-code">
                    <validation-provider
                      #default="{ errors }"
                      name="company-code"
                      rules="required"
                    >
                      <b-form-input
                        v-on:keypress="isLetter($event)"
                        v-on:paste="
                          (e) => {
                            e.preventDefault();
                            return false;
                          }
                        "
                        id="name-input"
                        v-model="newCompany.code"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Address" label-for="address">
                    <validation-provider
                      #default="{ errors }"
                      name="address"
                      rules="required"
                    >
                      <b-form-input
                        v-on:keypress="isLetter($event)"
                        v-on:paste="
                          (e) => {
                            e.preventDefault();
                            return false;
                          }
                        "
                        id="code-input"
                        v-model="newCompany.address"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="PoBox" label-for="poBox">
                    <validation-provider
                      #default="{ errors }"
                      name="poBox"
                      rules="required"
                    >
                      <b-form-input
                        v-on:keypress="isLetterandNumber($event)"
                        id="poBox-input"
                        v-model="newCompany.poBox"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group label="Email" label-for="email">
                    <validation-provider
                      #default="{ errors }"
                      name="email"
                      rules="required|email"
                    >
                      <b-form-input
                        id="email-input"
                        v-model="newCompany.email"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-button
                variant="primary"
                type="submit"
                @click.prevent="submitNewCompany"
              >
                Save
              </b-button>
            </form>
          </validation-observer>
        </b-modal>
      </b-col>
      <b-col>
        <b-modal
          id="modal-editCompany"
          ref="modal"
          title="Edit Company information"
          size="lg"
          hide-footer
        >
          <validation-observer ref="simpleRules">
            <form ref="form">
              <b-row>
                <b-form-input v-model="editCompany.id" hidden />

                <b-col md="6">
                  <b-form-group label="Name" label-for="name">
                    <validation-provider
                      #default="{ errors }"
                      name="name"
                      rules="required"
                    >
                      <b-form-input
                        id="name-input"
                        v-model="editCompany.name"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Company Code" label-for="company-code">
                    <validation-provider
                      #default="{ errors }"
                      name="company-code"
                      rules="required"
                    >
                      <b-form-input
                        id="name-input"
                        v-model="editCompany.code"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="Address" label-for="address">
                    <validation-provider
                      #default="{ errors }"
                      name="address"
                      rules="required"
                    >
                      <b-form-input
                        id="code-input"
                        v-model="editCompany.address"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group label="PoBox" label-for="poBox">
                    <validation-provider
                      #default="{ errors }"
                      name="poBox"
                      rules="required"
                    >
                      <b-form-input
                        id="poBox-input"
                        v-model="editCompany.poBox"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col md="6">
                  <b-form-group label="Email" label-for="email">
                    <validation-provider
                      #default="{ errors }"
                      name="email"
                      rules="required"
                    >
                      <b-form-input
                        id="email-input"
                        v-model="editCompany.email"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-button
                variant="primary"
                type="submit"
                class="felx float-right"
                @click.prevent="submitEditCompany"
              >
                Save
              </b-button>
            </form>
          </validation-observer>
        </b-modal>
      </b-col>

      <b-overlay :show="isLoading" no-wrap rounded="sm" />
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BTable,
  BPagination,
  BFormGroup,
  BCard,
  BOverlay,
  BDropdownItem,
  BDropdown,
  BButton,
  BFormSelect,
} from "bootstrap-vue";
import { required } from "@validations";

import { ValidationProvider, ValidationObserver } from "vee-validate";
import axios from "@axios";

export default {
  components: {
    BRow,
    BCol,
    BFormInput,
    BTable,
    BPagination,
    BFormGroup,
    BCard,
    BOverlay,
    BDropdownItem,
    BDropdown,
    ValidationProvider,
    ValidationObserver,
    BButton,
    BFormSelect,
  },
  data() {
    return {
      required,
      isLoading: false,
      props: ["items"],
      fields: ["name", "id", "code", "address", "poBox", "email", "Actions"],
      items: [],
      filter: "",

      perPage: 10,
      currentPage: 1,
      newCompany: {
        name: "",
        address: "",
        poBox: "",
        email: "",
      },
      editCompany: {
        id: 0,
        name: "",
        address: "",
        poBox: "",
        email: "",
      },
    };
  },
  computed: {
    rows() {
      return this.items.length;
    },
  },
  mounted() {
    try {
      this.fetchData();
    } catch (error) {
      console.error(error);
    }
  },
  methods: {
    isLetterandNumber(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (/^[a-zA-Z0-9_.,\s]*$/.test(char)) return true;
      // Match with regex
      else e.preventDefault(); // If not match, don't add to input text
    },
    isLetter(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (/^[a-zA-Z\s]*$/.test(char)) return true;
      // Match with regex
      else e.preventDefault(); // If not match, don't add to input text
    },
    isEmailAddress(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if (
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          char
        )
      )
        return true;
      // Match with regex
      else e.preventDefault(); // If not match, don't add to input text
    },
    deleteCompany(id, name) {
      this.$bvModal
        .msgBoxConfirm(`Are you sure to Delete  ${name}`, {
          title: "Confirmation",
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          headerBgVariant: "warning",
          okTitle: "YES",
          cancelTitle: "NO",
          footerClass: "p-2",
          hideHeaderClose: true,
          centered: true,
        })
        .then((value) => {
          this.boolValue = value;
          if (value) {
            this.isLoading = true;
            axios
              .delete(`/Merchant/api/V1.0/Merchants/DeleteCompany?id=${id}`)
              .then((res) => {
                this.isLoading = false;
                this.$bvModal
                  .msgBoxOk(`${res.message}`, {
                    title: "Success",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "success",
                    headerClass: "p-2 border-bottom-0",
                    footerClass: "p-2 border-top-0",
                    centered: true,
                  })
                  .then((result) => {
                    if (result) {
                      this.fetchData();
                    }
                  });
              })
              .catch((res) => {
                this.isLoading = false;

                this.$bvModal.msgBoxOk(`${res.data.message}`, {
                  title: "error",
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "success",
                  headerClass: "p-2 border-bottom-0",
                  footerClass: "p-2 border-top-0",
                  centered: true,
                });
              });
          }
        });
    },
    editCompanyModal(id) {
      const item = this.items.filter((i) => i.id === id);
      this.editCompany = item[0];
      this.$bvModal.show("modal-editCompany");
    },

    submitNewCompany() {
      this.$refs.simpleRules.validate().then((success) => {
        if (success) {
          this.$bvModal.hide("modal-newCompany");
          this.isLoading = true;
          axios
            .post("/Merchant/api/V1.0/Merchants/CreateCompany", this.newCompany)
            .then((res) => {
              this.isLoading = false;
              this.$bvModal
                .msgBoxOk(`${res.message}`, {
                  title: "Success",
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "success",
                  headerClass: "p-2 border-bottom-0",
                  footerClass: "p-2 border-top-0",
                  centered: true,
                })
                .then((result) => {
                  if (result) {
                    this.newCompany.name = "";
                    this.newCompany.address = "";
                    this.newCompany.email = "";
                    this.newCompany.poBox = "";

                    this.fetchData();
                  }
                });
            })
            .catch((res) => {
              this.isLoading = false;
              this.$bvModal.msgBoxOk(res.data.message, {
                title: "Error",
                size: "sm",
                buttonSize: "sm",
                okVariant: "success",
                headerClass: "p-2 border-bottom-0",
                footerClass: "p-2 border-top-0",
                centered: true,
              });
            });
        }
      });
    },
    submitEditCompany() {
      this.$refs.simpleRules.validate().then((success) => {
        if (success) {
          this.$bvModal.hide("modal-editCompany");
          this.isLoading = true;
          axios
            .put("/Merchant/api/V1.0/Merchants/UpdateCompany", this.editCompany)
            .then((res) => {
              this.isLoading = false;
              this.$bvModal
                .msgBoxOk(`${res.message}`, {
                  title: "Success",
                  size: "sm",
                  buttonSize: "sm",
                  okVariant: "success",
                  headerClass: "p-2 border-bottom-0",
                  footerClass: "p-2 border-top-0",
                  centered: true,
                })
                .then((result) => {
                  if (result) {
                    this.fetchData();
                  }
                });
            })
            .catch((res) => {
              this.isLoading = false;
              this.$bvModal.msgBoxOk(res.data.message, {
                title: "Error",
                size: "sm",
                buttonSize: "sm",
                okVariant: "success",
                headerClass: "p-2 border-bottom-0",
                footerClass: "p-2 border-top-0",
                centered: true,
              });
            });
        }
      });
    },
    fetchData() {
      this.isLoading = true;
      this.$store.dispatch("config/getActionPrivilege", "Companies-View");
      axios
        .get("/Merchant/api/V1.0/Merchants/GetAllCompanies")
        .then((res) => {
          this.items = res.companies;
          this.isLoading = false;
        })
        .catch((res) => {
          console.log(res);
          this.isLoading = false;
        });
    },
  },
};
</script>
